import React from 'react';

export const toString = (value) => {
  return value === null ? '' : `${value}`;
};

export const capitalize = (string) => {
  const stringified = toString(string);
  return (
    stringified && stringified.charAt(0).toUpperCase() + stringified.slice(1)
  );
};

export const toSentenceCase = (input) => {
  const words = input.split(' ');
  const output = [words[0]];
  for (let ii = 1, nn = words.length; ii < nn; ii++) {
    const word = words[ii];
    switch (word) {
      case 'PV':
        output.push(word);
        break;
      default:
        output.push(word.toLowerCase());
        break;
    }
  }
  return output.join(' ');
};

export const htmlEscape = (input) => {
  if (!input) {
    return input;
  }
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return input.replace(/[&<>"']/g, (tag) => {
    return map[tag] || tag;
  });
};

export const htmlUnescape = (input) => {
  if (!input) {
    return input;
  }
  const map = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#34;': '"',
  };
  return input.replace(/(&amp;|&lt;|&gt;|&quot;|&#39;|&#34;)/g, (tag) => {
    return map[tag] || tag;
  });
};

export const markdownToJsx = (markdown, options = {}) => {
  // TODO: switch to a lib
  const group = (str) => `(?:${str})`;
  const capturingGroup = (str) => `(${str})`;
  const matchGroups = (str) => str.replace(/\.\*\?/g, '(.*?)');
  const openingWhitespace = group('^|\\s+');
  const closingWhitespace = group('\\s+|$');

  const link = '\\[.*?\\]\\(.*?\\)';
  const bold = `${openingWhitespace}\\*.*?\\*${closingWhitespace}`;
  const boldLink = `${openingWhitespace}\\*${link}\\*${closingWhitespace}`;
  const italic = `${openingWhitespace}_.*?_${closingWhitespace}`;

  const blockElementSeparator = /\r\n\r\n|\n\n/;
  const inlineElements = [link, bold, italic];
  const inlineElementSeparator = RegExp(
    capturingGroup(inlineElements.map(group).join('|')),
  );

  const style = options.style || (() => ({}));
  const target =
    options.targetBlank === true || options.targetBlank === undefined
      ? '_blank'
      : null;

  const sanitizeUrl = (url) => {
    // from markdown-to-jsx package
    try {
      const decoded = decodeURIComponent(url).replace(/[^A-Za-z0-9/:]/g, '');
      // decodeURIComponent sometimes throws a URIError
      // See `decodeURIComponent('a%AFc');`
      // http://stackoverflow.com/questions/9064536/javascript-decodeuricomponent-malformed-uri-exception

      if (decoded.match(/^\s*(javascript|vbscript|data):/i)) {
        return null;
      }
    } catch (error) {
      return null;
    }

    try {
      return encodeURI(url);
    } catch (error) {
      return null;
    }
  };

  const transformBlock = (block, index) => {
    const elements = block
      .trim()
      .split(inlineElementSeparator)
      .map((element, elIndex) => {
        let match = element.match(matchGroups(boldLink));
        if (match) {
          const [all, title, href] = match;

          return (
            <a
              href={sanitizeUrl(href)}
              key={elIndex}
              rel={'noopener noreferrer'}
              style={{
                ...style('a'),
                fontWeight: 'bold',
                marginRight: '0.25em',
              }}
              target={target}
              title={title}>
              {title}
            </a>
          );
        }
        match = element.match(matchGroups(link));
        if (match) {
          const [all, title, href] = match;

          return (
            <a
              href={sanitizeUrl(href)}
              key={elIndex}
              rel={'noopener noreferrer'}
              style={style('a')}
              target={target}
              title={title}>
              {title}
            </a>
          );
        }
        match = element.match(matchGroups(bold));
        if (match) {
          const [all, text] = match;

          return (
            <strong key={elIndex} style={style('strong')}>
              &nbsp;{text}&nbsp;
            </strong>
          );
        }
        match = element.match(matchGroups(italic));
        if (match) {
          const [all, text] = match;

          return (
            <em key={elIndex} style={style('em')}>
              &nbsp;{text}&nbsp;
            </em>
          );
        }
        return `${element} `;
      });

    return (
      <p key={index + 1} style={style('em')}>
        {elements}
      </p>
    );
  };

  return (markdown || '')
    .split(blockElementSeparator)
    .filter((block) => Boolean(block))
    .map(transformBlock);
};

export const kebabCase = (input) => {
  return input
    .split(/(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join('-');
};

export const snakeCase = (input) => {
  return input
    .split(/(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join('_');
};

export const camelCase = (input) => {
  return input.replace(/[-_](\w)/g, (val) => val[1].toUpperCase());
};

export const truncate = (input, maxChars = 60) => {
  switch (true) {
    case !input:
      return '';
    case input.length <= maxChars:
      return input;
    default:
      const ellipsis = '...';
      return input.slice(0, maxChars - ellipsis.length) + ellipsis;
  }
};

export const zeroPad = (input, size) => {
  const sign = Math.sign(input) === -1 ? '-' : '';
  return (
    sign +
    new Array(size)
      .concat([Math.abs(input)])
      .join('0')
      .slice(-size)
  );
};
